// components/HeroSection.js
import React from 'react';
import styled from 'styled-components';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Button } from '../styles/StyledComponents';

const Hero = styled.section`
  background: #000000;
  color: white;
  min-height: 100vh;
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    min-height: 80vh;
  }
`;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr minmax(0, 1350px) 1fr;
  width: 100%;
  padding: 0 1rem;

  > div {
    grid-column: 2 / 3;
  }

  @media (max-width: 992px) {
    grid-template-columns: 1fr minmax(0, 800px) 1fr;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr minmax(0, 600px) 1fr;
    padding: 0 2rem;
  }

  @media (max-width: 480px) {
    padding: 0 1rem;
  }
`;

const HeroContentWrapper = styled(motion.div)`
  width: 100%;
  max-width: 1000px;
`;

const HeroTitle = styled.h1`
  font-size: 62px;
  font-weight: 600;
  letter-spacing: -2.5px;
  line-height: 1.1;
  color: white;
  margin: 0 0 2rem 0;
  width: 100%;
  max-width: 90%;

  span {
    color: rgb(200, 171, 71);
  }

  @media (max-width: 992px) {
    max-width: 85%;
  }

  @media (max-width: 768px) {
    max-width: 100%;
    font-size: clamp(1rem, 10vw, 3rem);
  }
`;

const ScrollArrow = styled(motion.div)`
  position: absolute;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  cursor: pointer;
  color: #C8AB48;

  svg {
    width: 36px;
    height: 52px;
    stroke: #C8AB48;
    stroke-width: 1.8;
    transition: all 0.3s ease;
  }

  &:hover svg {
    stroke: #ffffff;
    filter: drop-shadow(0 0 12px rgba(200, 171, 72, 0.6));
  }

  @media (max-width: 768px) {
    bottom: 30px;
  }
`;

const heroVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.8,
      staggerChildren: 0.3,
      delayChildren: 0.2,
    },
  },
};

const titleVariants = {
  hidden: { opacity: 0, y: 80 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 1, ease: 'easeOut' },
  },
};

const HeroSection = () => {
  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 500], [0, 180]);
  const y2 = useTransform(scrollY, [0, 500], [0, -120]);
  const y3 = useTransform(scrollY, [0, 500], [0, 100]);
  const rotate = useTransform(scrollY, [0, 500], [0, 15]);

  return (
    <Hero>
      <HeroGrid>
        <div>
          <HeroContentWrapper
            variants={heroVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div variants={titleVariants}>
              <HeroTitle>
                Where <span>visionary brands</span> meet <br/>world class teams
              </HeroTitle>
            </motion.div>
          </HeroContentWrapper>
        </div>
      </HeroGrid>

      <ScrollArrow
        animate={{ y: [0, 12, 0] }}
        transition={{ repeat: Infinity, duration: 1.6, ease: 'easeInOut' }}
        onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
      >
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M12 5v14m-7-7l7 7 7-7" />
        </svg>
      </ScrollArrow>
    </Hero>
  );
};

export default HeroSection;